import type { ApiErrorBody, ErrorCode, SessionResponse } from '@obras/shared';

/*
 * `import.meta.env` chega como `any`, então o valor é estreitado antes de
 * virar URL: sem isso, um `.env` com a chave escrita errado passaria adiante
 * um `undefined` disfarçado e o erro só apareceria na primeira requisição.
 */
const configurada: unknown = import.meta.env['VITE_API_URL'];
const API_URL = (
  typeof configurada === 'string' && configurada !== '' ? configurada : 'http://localhost:3333'
).replace(/\/$/, '');

/**
 * O access token vive apenas em memória.
 *
 * Guardá-lo em localStorage o exporia a qualquer XSS. A sessão sobrevive a
 * recarregamentos pelo refresh token, que está em cookie httpOnly e é
 * inacessível a JavaScript.
 */
let accessToken: string | null = null;
let onSessionLost: (() => void) | null = null;

export function setAccessToken(token: string | null): void {
  accessToken = token;
}

export function setSessionLostHandler(handler: (() => void) | null): void {
  onSessionLost = handler;
}

export class ApiError extends Error {
  constructor(
    readonly code: ErrorCode,
    message: string,
    readonly details?: Record<string, string[]>,
    readonly status?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Mensagens de erro do campo, para exibir sob o input correspondente. */
  fieldErrors(): Record<string, string> {
    const output: Record<string, string> = {};
    for (const [field, messages] of Object.entries(this.details ?? {})) {
      const first = messages[0];
      if (first) output[field] = first;
    }
    return output;
  }
}

interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  /** Envia FormData sem serializar em JSON (upload de arquivo). */
  readonly formData?: FormData;
}

async function toApiError(response: Response): Promise<ApiError> {
  let body: ApiErrorBody | null = null;
  try {
    body = (await response.json()) as ApiErrorBody;
  } catch {
    // Resposta sem corpo JSON (proxy, gateway) cai na mensagem genérica abaixo.
  }

  return new ApiError(
    body?.error.code ?? 'INTERNAL',
    body?.error.message ?? 'Não foi possível concluir a operação.',
    body?.error.details,
    response.status,
  );
}

let refreshInFlight: Promise<SessionResponse | null> | null = null;

/**
 * Renova a sessão e devolve a sessão renovada, ou `null` se não houver mais
 * sessão válida.
 *
 * Chamadas concorrentes compartilham a mesma promessa. Sem isso, duas rotações
 * do mesmo token saem juntas e o servidor as lê como reuso — o que derruba a
 * família inteira e desloga o usuário. Todo caminho que renova sessão passa
 * por aqui, inclusive a tentativa silenciosa da carga inicial.
 */
async function refreshSession(): Promise<SessionResponse | null> {
  refreshInFlight ??= (async () => {
    try {
      const response = await fetch(`${API_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) return null;

      const session = (await response.json()) as SessionResponse;
      accessToken = session.accessToken;
      return session;
    } catch {
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function send(path: string, options: RequestOptions, retrying = false): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`;
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';

  const init: RequestInit = {
    method: options.method ?? 'GET',
    headers,
    credentials: 'include',
  };
  if (options.signal) init.signal = options.signal;
  if (options.formData) init.body = options.formData;
  else if (options.body !== undefined) init.body = JSON.stringify(options.body);

  const response = await fetch(`${API_URL}${path}`, init);

  // Access token expirado: renova uma vez e repete a requisição original.
  if (response.status === 401 && !retrying && !path.startsWith('/auth/refresh')) {
    const renewed = await refreshSession();
    if (renewed) return send(path, options, true);

    accessToken = null;
    onSessionLost?.();
  }

  return response;
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const response = await send(path, options);

  if (!response.ok) throw await toApiError(response);
  if (response.status === 204) return undefined as T;

  return (await response.json()) as T;
}

export const api = {
  get: <T>(path: string, signal?: AbortSignal): Promise<T> =>
    apiRequest<T>(path, signal ? { signal } : {}),
  post: <T>(path: string, body?: unknown): Promise<T> =>
    apiRequest<T>(path, { method: 'POST', ...(body !== undefined ? { body } : {}) }),
  patch: <T>(path: string, body: unknown): Promise<T> =>
    apiRequest<T>(path, { method: 'PATCH', body }),
  delete: <T>(path: string): Promise<T> => apiRequest<T>(path, { method: 'DELETE' }),
  upload: <T>(path: string, file: File): Promise<T> => {
    const formData = new FormData();
    formData.append('file', file);
    return apiRequest<T>(path, { method: 'POST', formData });
  },

  /**
   * Baixa um arquivo gerado pela API.
   *
   * Não dá para apontar um `<a href>` direto para a rota: o token de acesso
   * vive em memória e viaja no cabeçalho, que um link do navegador não manda.
   * Então o arquivo vem por `fetch`, vira blob e só aí o download dispara.
   */
  download: async (path: string, fallbackName: string): Promise<void> => {
    const response = await send(path, {});
    if (!response.ok) throw await toApiError(response);

    const disposition = response.headers.get('Content-Disposition') ?? '';
    const nome = /filename="([^"]+)"/.exec(disposition)?.[1] ?? fallbackName;

    const url = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = url;
    link.download = nome;
    link.click();
    URL.revokeObjectURL(url);
  },
};

/** Monta querystring ignorando valores vazios, para não poluir a URL. */
export function queryString(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams();

  for (const [key, value] of Object.entries(params)) {
    if (value === undefined || value === null || value === '') continue;
    search.set(key, String(value));
  }

  const result = search.toString();
  return result ? `?${result}` : '';
}

export { API_URL, refreshSession };
